'use client';
/**
 * CountryContext - global market awareness for ScriptUnlock
 *
 * Source of truth: src/lib/globalMarkets.ts
 * Covers: US (live), AU (live), GB (beta), CA/NZ (Q3 2026), + 10 planned markets
 *
 * Detection order:
 *  1. localStorage ('selectedCountry') - user's explicit choice, always wins
 *  2. pharmacy.country from DB - for logged-in pharmacies
 *  3. Timezone soft-detect - silent, never shown to user
 *  4. Default: US
 *
 * Auto-detection is intentionally soft: it sets a default but never
 * flashes currency or overrides a saved preference.
 */
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import {
  getMarket,
  getCountryFromTimezone,
  getAvailableMarkets,
  getAllMarkets,
  getDefaultLanguageForCountry,
  MARKETS,
  type Market,
} from '@/lib/globalMarkets';
import { setMarketLanguage } from '@/i18n';

// Re-export Market type for consumers
export type { Market };
export type { Market as CountryConfig }; // backwards compat alias

interface CountryContextType {
  country: Market;
  setCountryCode: (code: string) => void;
  availableCountries: Market[];   // live + beta only (shown in UI)
  allCountries: Market[];         // all including planned (for admin/roadmap)
  isLoading: boolean;
  isLive: boolean;                // true if current market is live/beta
}

const CountryContext = createContext<CountryContextType | undefined>(undefined);

export const CountryProvider = ({ children }: { children: ReactNode }) => {
  const [country, setCountry] = useState<Market>(MARKETS['US']);
  const [isLoading, setIsLoading] = useState(true);

  const setCountryCode = (code: string) => {
    const market = getMarket(code);
    setCountry(market);
    localStorage.setItem('selectedCountry', market.code);
    // Auto-switch UI language to the market default
    const preferredLang = localStorage.getItem('userPickedLanguage');
    if (!preferredLang) {
      // Only auto-switch if user hasn't manually picked a language
      setMarketLanguage(getDefaultLanguageForCountry(code));
    }
  };

  useEffect(() => {
    // Priority 1: user's saved preference
    const saved = localStorage.getItem('selectedCountry');
    if (saved && MARKETS[saved]) {
      setCountry(MARKETS[saved]);
      setIsLoading(false);
      return;
    }

    // Priority 2: soft timezone detection (never flashes - sets silent default)
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const detected = getCountryFromTimezone(tz);
      if (detected && MARKETS[detected]) {
        setCountry(MARKETS[detected]);
      }
    } catch {
      // Timezone API unavailable - stay on US default
    }

    setIsLoading(false);
  }, []);

  const isLive = country.launch_status === 'live' || country.launch_status === 'beta';

  return (
    <CountryContext.Provider value={{
      country,
      setCountryCode,
      availableCountries: getAvailableMarkets(),
      allCountries: getAllMarkets(),
      isLoading,
      isLive,
    }}>
      {children}
    </CountryContext.Provider>
  );
};

export const useCountry = () => {
  const context = useContext(CountryContext);
  if (!context) throw new Error('useCountry must be used within a CountryProvider');
  return context;
};

// Backwards compat - countryConfigs consumers can use this
export const countryConfigs = MARKETS;
export const defaultConfig = MARKETS['US'];
